/**
 * @file 数値のフォーマットや計算に関するユーティリティ関数を提供します。
 * パーセンテージ変換、数値の省略表示、ウィルソン信頼区間計算、時間フォーマットなどの機能を含みます。
 */

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

/**
 * パーセンテージを返す
 * decimalPlaces で小数点以下の桁数を指定できる（デフォルトは2）
 * @example
 * const value = 0.123456789;
 * console.log(percentage(value)); // -> 12.35%
 */
export function percentage(value: unknown, decimalPlaces = 2) {
  if (typeof value !== "number" || isNaN(value) || value === Infinity) {
    return "N/A";
  }
  return `${(value * 100).toFixed(decimalPlaces)}%`;
}

/**
 * 指標値をK/M/B/Tなどで省略表示する。
 * decimalPlaces（デフォルト2）で各単位の値を丸めてから、1,000以上なら次の単位へ進む。
 * 例: 999_999は2桁なら1M、3桁なら999.999K。Adsに限らず全呼び出し元で共通の契約。
 * 小数部分の末尾の0は省略し、整数部分の0は保持する。十億はB（Billion）で表し、カード・チャート・表で単位を揃える。
 * @example
 * const value = 123456789;
 * console.log(formatCompactNumber(value)); // -> 123.46M
 *
 * const value = 10000;
 * console.log(formatCompactNumber(value)); // -> 10K
 */
export function formatCompactNumber(value: number, decimalPlaces = 2) {
  const units = ["", "K", "M", "B", "T", "P", "E", "Z", "Y"];
  let unit: string | undefined = "";
  let index = 0;
  while (
    Number(value.toFixed(decimalPlaces)) >= 1000 &&
    index < units.length - 1
  ) {
    value /= 1000;
    index++;
  }
  unit = units[index];
  if (unit === undefined) {
    throw new Error("unit is undefined");
  }
  return `${Number(value.toFixed(decimalPlaces))}${unit}`;
}

/**
 * Calculate the wilson 95% confidence interval.
 * @param positiveScore  The number of positive scores (indexedCount or crawledCount)
 * @param total The total number of scores (samplingCount)
 * @returns [lowerBound, upperBound] The lower and upper bound of the confidence interval.
 */
export const wilsonScoreInterval = (
  positiveScore?: number,
  total?: number,
): [number, number] => {
  if (positiveScore === undefined || total === undefined || total === 0) {
    return [0, 0];
  }

  // z is the z-score for a 95% confidence interval (1.96)
  const z = 1.96;

  // p̂ is the proportion of positive scores
  const phat = positiveScore / total;

  const lowerBound =
    (phat +
      z ** 2 / (2 * total) -
      z * Math.sqrt((phat * (1 - phat) + z ** 2 / (4 * total)) / total)) /
    (1 + z ** 2 / total);

  const upperBound =
    (phat +
      z ** 2 / (2 * total) +
      z * Math.sqrt((phat * (1 - phat) + z ** 2 / (4 * total)) / total)) /
    (1 + z ** 2 / total);

  return [lowerBound, upperBound];
};

dayjs.extend(duration);

/**
 * 秒数をHH:mm:ss形式の文字列にフォーマットする
 * @param seconds - 秒数
 * @returns フォーマットされた文字列
 */
export function formatDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return "-";
  }
  return dayjs.duration(seconds, "seconds").format("HH:mm:ss");
}
